import React, { useState, useEffect, useMemo } from 'react';
import { useData } from '../DataContext';
import { formatRupiah } from '../data/financialData';
import type { Area } from '../data/financialData';
import { parseGLRows, GLRow } from '../utils/budgetMatch';

interface PNLPivotTableProps {
  area: Area;
  branch: string;
  startIdx: number;
  endIdx: number;
  activeEntity?: string;
  selectedBranches?: string[];
}

const CATEGORY_DEFINITIONS = [
  { id: 'rev', label: '01.OPERATING REVENUE', prefixes: ['10'] },
  { id: 'cogs', label: '02.DIRECT COST', prefixes: ['11'] },
  { id: 'sal', label: '03.OPR EXP SALARY', prefixes: ['121', '122', '123', '124', '125'] },
  { id: 'ruko', label: '04.RUKO EXP', prefixes: ['126'] },
  { id: 'depre', label: '05.DEPRE_EXP', prefixes: ['127'] },
  { id: 'other', label: '09.OTHER EXPENSES', prefixes: ['15'] },
];

export const PNLPivotTable: React.FC<PNLPivotTableProps> = ({ area, branch, startIdx, endIdx, activeEntity, selectedBranches }) => {
  const { MONTHS, activeEntity: contextActiveEntity } = useData() as any;
  const resolvedEntity = activeEntity || contextActiveEntity || 'SAHABAT_GADAI';

  const [glData, setGlData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Drilldown state
  const [showModal, setShowModal] = useState(false);
  const [drilldownTitle, setDrilldownTitle] = useState('');
  const [drilldownRows, setDrilldownRows] = useState<GLRow[]>([]);
  const [modalMonthIdx, setModalMonthIdx] = useState<number>(0);

  useEffect(() => {
    let mounted = true;
    const loadGL = async () => {
      const currentMonths = MONTHS.slice(startIdx, endIdx + 1);
      if (currentMonths.length === 0) return;

      setLoading(true);
      try {
        const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';
        const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';

        const qParams = new URLSearchParams({
          entity: resolvedEntity,
          months: currentMonths.map((m: any) => m.month).join(','),
          acc_code_prefixes: '10,11,121,122,123,124,125,126,127,15'
        });

        if (branch && branch !== 'ALL') {
          qParams.set('branch', branch);
        } else if (area && area !== 'ALL') {
          qParams.set('area', area);
        }

        const res = await fetch(`${API_BASE}/api/gl-transactions?${qParams.toString()}`, {
          headers: { 'Authorization': `Bearer ${API_KEY}` }
        });
        if (!res.ok) throw new Error('Gagal memuat GL data dari cloud');
        const data = await res.json();
        if (mounted) setGlData(data);
      } catch (err) {
        console.error('Failed to load GL data for pivot table:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadGL();
    return () => { mounted = false; };
  }, [resolvedEntity, startIdx, endIdx, branch, area, MONTHS]);

  const targetMonths = useMemo(() => MONTHS.slice(startIdx, endIdx + 1), [MONTHS, startIdx, endIdx]);

  const pivotData = useMemo(() => {
    if (!glData) return null;
    const parsed = parseGLRows(glData.data);
    
    // Filter by branch or area
    const filtered = parsed.filter(row => {
      if (selectedBranches && selectedBranches.length > 0) {
        return selectedBranches.includes(row.branch);
      }
      if (branch !== 'ALL') {
        return row.branch === branch;
      }
      if (area !== 'ALL') {
        return row.area === area;
      }
      return true;
    });

    // Grouping structure
    const groups: Record<string, {
      totalByMonth: number[];
      grandTotal: number;
      accounts: Record<string, {
        name: string;
        byMonth: number[];
        total: number;
      }>;
    }> = {};

    CATEGORY_DEFINITIONS.forEach(def => {
      groups[def.id] = {
        totalByMonth: Array(targetMonths.length).fill(0),
        grandTotal: 0,
        accounts: {}
      };
    });

    // Process rows
    filtered.forEach(row => {
      // Find month index
      const mIdx = targetMonths.findIndex((m: any) => m.month === row.month);
      if (mIdx === -1) return; // Outside selected range

      // Find category
      const category = CATEGORY_DEFINITIONS.find(def => 
        def.prefixes.some(p => row.accCode.startsWith(p))
      );

      if (category) {
        const grp = groups[category.id];
        let amt = row.amount;
        
        // Income is usually credit-positive in GL, Expenses debit-positive.
        // We normalize them to positive for display.
        if (category.id === 'rev' || category.id === 'other') { // wait, other expenses is expense
           if (category.id === 'rev') amt = row.credit - row.debit;
           else amt = row.debit - row.credit;
        } else {
           amt = row.debit - row.credit;
        }

        if (amt === 0) return;

        // Add to group total
        grp.totalByMonth[mIdx] += amt;
        grp.grandTotal += amt;

        // Add to account total
        if (!grp.accounts[row.accCode]) {
          grp.accounts[row.accCode] = {
            name: row.accName,
            byMonth: Array(targetMonths.length).fill(0),
            total: 0
          };
        }
        grp.accounts[row.accCode].byMonth[mIdx] += amt;
        grp.accounts[row.accCode].total += amt;
      }
    });

    return groups;
  }, [glData, branch, area, targetMonths, selectedBranches]);

  const toggleGroup = (id: string) => {
    setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDrilldownClick = (title: string, codes: string[], monthIndex: number) => {
    if (!glData) return;
    const monthObj = targetMonths[monthIndex];
    if (!monthObj) return;

    setDrilldownTitle(title);
    setModalMonthIdx(startIdx + monthIndex); // Actual global index
    setDrilldownRows([]);
    setShowModal(true);

    const targetMonthStr = monthObj.month;
    const parsedAll = parseGLRows(glData.data);

    const filtered = parsedAll.filter((row) => {
      if (row.month !== targetMonthStr) return false;
      if (selectedBranches && selectedBranches.length > 0) {
        if (!selectedBranches.includes(row.branch)) return false;
      } else if (branch !== 'ALL') {
        if (row.branch !== branch) return false;
      } else if (area !== 'ALL') {
        if (row.area !== area) return false;
      }
      return codes.some(c => row.accCode.startsWith(c));
    });

    setDrilldownRows(filtered);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
        <div className="spinner" style={{ margin: '0 auto 16px', width: '24px', height: '24px', borderRadius: '50%', border: '2px solid var(--border-light)', borderTopColor: 'var(--emerald-base)', animation: 'spin 1s linear infinite' }} />
        Memuat data PNL Historis...
      </div>
    );
  }

  if (!pivotData) return null;

  // Calculate grand total (Net Profit equivalent if categories cover it all)
  const grandTotalByMonth = Array(targetMonths.length).fill(0);
  let overallGrandTotal = 0;
  targetMonths.forEach((_: any, mIdx: number) => {
    const rev = pivotData['rev'].totalByMonth[mIdx] || 0;
    const exp = CATEGORY_DEFINITIONS.filter(d => d.id !== 'rev').reduce((sum, d) => sum + (pivotData[d.id].totalByMonth[mIdx] || 0), 0);
    grandTotalByMonth[mIdx] = rev - exp;
    overallGrandTotal += (rev - exp);
  });

  return (
    <div style={{ marginTop: '20px', backgroundColor: 'var(--bg-card)', borderRadius: '8px', border: '1px solid var(--border-light)', overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--bg-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>📋 Historical P&L</h3>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>*Klik nominal untuk melihat rincian jurnal</div>
      </div>
      
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table" style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse', whiteSpace: 'nowrap' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--bg-secondary)' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--border-color)', minWidth: '140px' }}>Account Category / Name</th>
              {targetMonths.map((m: any, i: number) => (
                <th key={i} style={{ textAlign: 'right', padding: '10px 12px', borderBottom: '2px solid var(--border-color)' }}>
                  {m.month}
                </th>
              ))}
              <th style={{ textAlign: 'right', padding: '10px 12px', borderBottom: '2px solid var(--border-color)', backgroundColor: 'var(--bg-highlight)' }}>Grand Total</th>
            </tr>
          </thead>
          <tbody>
            {CATEGORY_DEFINITIONS.map(def => {
              const grp = pivotData[def.id];
              const isExpanded = expandedGroups[def.id];
              const accounts = Object.keys(grp.accounts).sort();
              
              if (grp.grandTotal === 0 && accounts.length === 0) return null;

              return (
                <React.Fragment key={def.id}>
                  {/* Group Header */}
                  <tr style={{ backgroundColor: 'var(--bg-highlight)', borderBottom: '1px solid var(--border-color)', fontWeight: '600' }}>
                    <td 
                      style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                      onClick={() => toggleGroup(def.id)}
                    >
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{isExpanded ? '▼' : '▶'}</span>
                      {def.label}
                    </td>
                    {grp.totalByMonth.map((amt: number, i: number) => (
                      <td 
                        key={i} 
                        style={{ textAlign: 'right', padding: '8px 12px', cursor: amt !== 0 ? 'pointer' : 'default', color: amt !== 0 ? 'var(--blue-base)' : 'inherit' }}
                        onClick={() => amt !== 0 && handleDrilldownClick(def.label, def.prefixes, i)}
                        title="Klik untuk drilldown jurnal"
                      >
                        {amt !== 0 ? formatRupiah(amt) : '-'}
                      </td>
                    ))}
                    <td style={{ textAlign: 'right', padding: '8px 12px', backgroundColor: 'var(--bg-highlight)' }}>
                      {grp.grandTotal !== 0 ? formatRupiah(grp.grandTotal) : '-'}
                    </td>
                  </tr>

                  {/* Sub Accounts */}
                  {isExpanded && accounts.map(accCode => {
                    const acc = grp.accounts[accCode];
                    return (
                      <tr key={accCode} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '8px 12px', paddingLeft: '32px', color: 'var(--text-secondary)' }}>
                          {accCode} {acc.name}
                        </td>
                        {acc.byMonth.map((amt: number, i: number) => (
                          <td 
                            key={i} 
                            style={{ textAlign: 'right', padding: '8px 12px', cursor: amt !== 0 ? 'pointer' : 'default', color: amt !== 0 ? 'var(--blue-base)' : 'var(--text-muted)' }}
                            onClick={() => amt !== 0 && handleDrilldownClick(`${accCode} ${acc.name}`, [accCode], i)}
                            title="Klik untuk drilldown jurnal"
                          >
                            {amt !== 0 ? formatRupiah(amt) : '-'}
                          </td>
                        ))}
                        <td style={{ textAlign: 'right', padding: '8px 12px', backgroundColor: 'var(--bg-highlight)', color: 'var(--text-secondary)' }}>
                          {acc.total !== 0 ? formatRupiah(acc.total) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {/* Grand Total Row */}
            <tr style={{ backgroundColor: 'var(--bg-secondary)', borderTop: '2px solid var(--border-color)', fontWeight: '700' }}>
              <td style={{ padding: '12px' }}>Grand Total (Net Profit)</td>
              {grandTotalByMonth.map((amt: number, i: number) => (
                <td key={i} style={{ textAlign: 'right', padding: '12px', color: amt >= 0 ? 'var(--emerald-base)' : 'var(--red-base)' }}>
                  {formatRupiah(amt)}
                </td>
              ))}
              <td style={{ textAlign: 'right', padding: '12px', backgroundColor: 'var(--bg-highlight)', color: overallGrandTotal >= 0 ? 'var(--emerald-base)' : 'var(--red-base)' }}>
                {formatRupiah(overallGrandTotal)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* DRILLDOWN MODAL */}
      {showModal && (
        <div className="drill-modal-overlay" onClick={() => setShowModal(false)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div className="drill-modal" onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: 'var(--bg-card)', borderRadius: '12px', width: '90%', maxWidth: '1000px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{ padding: '20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📖</span> Drilldown Jurnal Keuangan
                </h3>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Detail transaksi untuk: <strong style={{ color: 'var(--text-primary)' }}>{drilldownTitle}</strong> pada <strong>{MONTHS[modalMonthIdx]?.label}</strong>
                </p>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '20px', cursor: 'pointer', padding: '4px' }}
              >
                ✕
              </button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '0 20px' }}>
              {drilldownRows.length === 0 ? (
                <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  Tidak ditemukan baris transaksi Buku Besar yang cocok.
                </div>
              ) : (
                <table className="data-table" style={{ width: '100%', fontSize: '12px', marginTop: '16px', marginBottom: '16px' }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1 }}>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '10px' }}>Tanggal</th>
                      <th style={{ textAlign: 'left', padding: '10px' }}>No. Jurnal</th>
                      <th style={{ textAlign: 'left', padding: '10px' }}>Akun</th>
                      <th style={{ textAlign: 'left', padding: '10px' }}>Deskripsi</th>
                      <th style={{ textAlign: 'left', padding: '10px' }}>Cabang</th>
                      <th style={{ textAlign: 'right', padding: '10px' }}>Debit</th>
                      <th style={{ textAlign: 'right', padding: '10px' }}>Kredit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drilldownRows.map((row, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{row.month}</td>
                        <td style={{ padding: '8px 10px', color: 'var(--text-muted)' }}>-</td>
                        <td style={{ padding: '8px 10px', fontWeight: '600' }}>{row.accName}</td>
                        <td style={{ padding: '8px 10px', color: 'var(--text-accent)', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.desc}>
                          {row.desc}
                        </td>
                        <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{row.branch}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{row.debit > 0 ? formatRupiah(row.debit) : '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{row.credit > 0 ? formatRupiah(row.credit) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--bg-secondary)', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Menampilkan <strong>{drilldownRows.length}</strong> baris transaksi.
              </span>
              <button 
                onClick={() => setShowModal(false)}
                style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600', backgroundColor: 'var(--blue-base)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                Tutup Rincian
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



